<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="page-header">
        <h1>GAP基地管理</h1>
        <div class="header-actions">
          <span class="total-count">共 {{ total }} 条记录</span>
          <input type="file" ref="excelInput" accept=".xlsx,.xls" @change="handleExcelUpload" style="display: none" />
          <button class="upload-btn" @click="$refs.excelInput.click()">
            <span>📤</span> 导入Excel
          </button>
          <button class="add-btn" @click="showAddModal = true">
            <span>➕</span> 添加数据
          </button>
        </div>
      </div>
      
      <div class="filter-bar">
        <!-- 搜索框 -->
        <div class="search-group">
          <input 
            type="text" 
            class="search-input" 
            placeholder="搜索药材名称..."
            v-model="searchText"
            @keyup.enter="handleSearch"
          />
          <button class="search-btn" @click="handleSearch">
            <span>🔍</span> 搜索
          </button>
        </div>
        
        <!-- 药材下拉 -->
        <div class="filter-item">
          <label>药材筛选：</label>
          <select v-model="selectedHerb" class="filter-select" @change="handleHerbChange">
            <option value="">全部药材</option>
            <option v-for="herb in herbsList" :key="herb" :value="herb">
              {{ herb }}
            </option>
          </select>
        </div>
        
        <!-- 省份下拉 -->
        <div class="filter-item">
          <label>省份筛选：</label>
          <select v-model="selectedProvince" class="filter-select" @change="handleProvinceChange">
            <option value="">全部省份</option>
            <option v-for="province in provincesList" :key="province" :value="province">
              {{ province }}
            </option>
          </select>
        </div>
        
        <!-- 操作按钮 -->
        <div class="filter-actions">
          <button class="reset-btn" @click="resetFilters">
            <span>↺</span> 重置
          </button>
          <button class="refresh-btn" @click="fetchList">
            <span>🔄</span> 刷新
          </button>
        </div>
      </div>
      
      <div class="content-body">
        <div class="loading" v-if="loading">
          <div class="loading-spinner"></div>
          <span>加载中...</span>
        </div>
        <div class="error" v-else-if="error">{{ error }}</div>
        
        <div class="table-container" v-else>
          <table class="data-table">
            <thead>
              <tr>
                <th class="th-id">序号</th>
                <th class="th-company">药企名称</th>
                <th class="th-herb">中药材品种</th>
                <th class="th-origin">基原/批号</th>
                <th class="th-medicine">中药品种</th>
                <th class="th-supplier">中药材生产企业</th>
                <th class="th-area">基地面积</th>
                <th class="th-address">基地地址</th>
                <th class="th-result">检查结果</th>
                <th class="th-notice">公示时间</th>
                <th class="th-announce">公布时间</th>
                <th class="th-yield">年产量</th>
                <th class="th-bureau">检查局</th>
                <th class="th-action">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in list" :key="item.id">
                <td class="td-id">{{ item.sequence || item.id }}</td>
                <td class="td-company">{{ item.company_name || '-' }}</td>
                <td class="td-herb">
                  <span class="herb-tag">{{ item.herb_name || '-' }}</span>
                </td>
                <td class="td-origin">{{ item.origin_or_batch || '-' }}</td>
                <td class="td-medicine">{{ item.medicine_variety || '-' }}</td>
                <td class="td-supplier">{{ item.supplier_name || '-' }}</td>
                <td class="td-area">
                  <span class="area-value">{{ item.base_area || '-' }}</span>
                  <span class="area-unit" v-if="item.base_area">亩</span>
                </td>
                <td class="td-address">{{ item.base_address || '-' }}</td>
                <td class="td-result">
                  <span :class="['status-tag', getResultClass(item.inspection_result)]">
                    {{ item.inspection_result || '-' }}
                  </span>
                </td>
                <td class="td-notice">{{ item.notice_time || '-' }}</td>
                <td class="td-announce">{{ item.announce_time || '-' }}</td>
                <td class="td-yield">
                  <span v-if="item.yield_amount">{{ item.yield_amount }} 吨</span>
                  <span v-else>-</span>
                </td>
                <td class="td-bureau">{{ item.inspection_bureau || '-' }}</td>
                <td class="td-action">
                  <button class="delete-btn" @click="handleDelete(item.id)">删除</button>
                </td>
              </tr>
              <tr v-if="list.length === 0">
                <td colspan="14" class="empty">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="pagination" v-if="!loading && !error">
          <div class="pagination-left">
            <span>共 {{ total }} 条记录</span>
            <select class="page-size-select" v-model="pageSize" @change="changePageSize">
              <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}条/页</option>
            </select>
          </div>
          <div class="pagination-right">
            <button 
              class="page-btn" 
              :disabled="currentPage === 1" 
              @click="changePage(1)"
            >首页</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === 1" 
              @click="changePage(currentPage - 1)"
            >上一页</button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages" 
              @click="changePage(currentPage + 1)"
            >下一页</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages" 
              @click="changePage(totalPages)"
            >尾页</button>
            <span class="page-jump">
              到第 <input 
                type="number" 
                class="page-input" 
                v-model.number="jumpPage" 
                @keyup.enter="changePage(jumpPage)"
                min="1" 
                :max="totalPages"
              /> 页
            </span>
          </div>
        </div>
      </div>
      
      <!-- 添加数据弹窗 -->
      <div class="modal-overlay" v-if="showAddModal" @click.self="showAddModal = false">
        <div class="modal-content">
          <div class="modal-header">
            <h2>添加GAP基地数据</h2>
            <button class="close-btn" @click="showAddModal = false">×</button>
          </div>
          <div class="modal-body">
            <div class="form-row">
              <div class="form-item">
                <label>药企名称 *</label>
                <input type="text" v-model="addForm.companyName" required placeholder="请输入药企名称" />
              </div>
              <div class="form-item">
                <label>中药材品种 *</label>
                <input type="text" v-model="addForm.herbName" required placeholder="如：金银花" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>基原/批号</label>
                <input type="text" v-model="addForm.originOrBatch" placeholder="基原或批号信息" />
              </div>
              <div class="form-item">
                <label>中药品种</label>
                <input type="text" v-model="addForm.medicineVariety" placeholder="中药品种" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
<label>中药材生产企业</label>
            <input type="text" v-model="addForm.supplierName" placeholder="中药材生产企业名称" />
              </div>
              <div class="form-item">
                <label>基地面积（亩）</label>
                <input type="number" v-model="addForm.baseArea" placeholder="基地面积" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item full-width">
                <label>基地地址</label>
                <textarea v-model="addForm.baseAddress" placeholder="基地详细地址" rows="3"></textarea>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>检查结果</label>
                <select v-model="addForm.inspectionResult">
                  <option value="符合要求">符合要求</option>
                  <option value="不符合要求">不符合要求</option>
                </select>
              </div>
              <div class="form-item">
                <label>年产量（吨）</label>
                <input type="text" v-model="addForm.yieldAmount" placeholder="年产量" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>公示时间</label>
                <input type="text" v-model="addForm.noticeTime" placeholder="如：2025-01-15" />
              </div>
              <div class="form-item">
                <label>公布时间</label>
                <input type="text" v-model="addForm.announceTime" placeholder="如：2025-01-20" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item full-width">
                <label>检查局</label>
                <input type="text" v-model="addForm.inspectionBureau" placeholder="检查局名称" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="cancel-btn" @click="showAddModal = false">取消</button>
            <button class="submit-btn" @click="handleAddData" :disabled="adding">
              {{ adding ? '添加中...' : '确认添加' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import { apiGet, apiClient, getAdminPassword } from '../utils/api'
import axios from 'axios'
import * as XLSX from 'xlsx'

export default {
  name: 'GapBasesList',
  components: { Sidebar },
  data() {
    return {
      list: [],
      loading: true,
      error: '',
      searchText: '',
      selectedHerb: '',
      selectedProvince: '',
      herbsList: [],
      provincesList: [],
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20],
      total: 0,
      jumpPage: 1,
      showAddModal: false,
      adding: false,
      uploading: false,
      addForm: {
        companyName: '',
        herbName: '',
        originOrBatch: '',
        medicineVariety: '',
        supplierName: '',
        baseArea: '',
        baseAddress: '',
        inspectionResult: '符合要求',
        noticeTime: '',
        announceTime: '',
        inspectionBureau: '',
        yieldAmount: ''
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  mounted() {
    this.fetchHerbsList()
    this.fetchProvincesList()
    this.fetchList()
  },
  methods: {
    async fetchHerbsList() {
      try {
        const response = await axios.get('/api/gap/herbs', {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          this.herbsList = response.data.data || []
        }
      } catch (err) {
        console.error('获取药材列表失败:', err)
      }
    },
    async fetchProvincesList() {
      try {
        const response = await axios.get('/api/gap/provinces', {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          this.provincesList = response.data.data || []
        }
      } catch (err) {
        console.error('获取省份列表失败:', err)
      }
    },
    async fetchList() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          search: this.searchText || undefined,
          herb: this.selectedHerb || undefined,
          province: this.selectedProvince || undefined
        }
        const response = await apiGet('/api/admin/gap-bases', params)
        if (response.data?.code === 200) {
          this.list = response.data.data?.list || []
          this.total = response.data.data?.total || 0
        } else {
          this.error = response.data?.msg || '获取数据失败'
        }
      } catch (err) {
        this.error = '获取数据失败，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    handleHerbChange() {
      this.currentPage = 1
      this.fetchList()
    },
    handleProvinceChange() {
      this.currentPage = 1
      this.fetchList()
    },
    resetFilters() {
      this.searchText = ''
      this.selectedHerb = ''
      this.selectedProvince = ''
      this.currentPage = 1
      this.fetchList()
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      this.fetchList()
    },
    changePageSize() {
      this.currentPage = 1
      this.fetchList()
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    getResultClass(result) {
      if (result === '符合要求') return 'passed'
      if (result && result.includes('不符合')) return 'failed'
      return 'other'
    },
    async handleAddData() {
      if (!this.addForm.companyName || !this.addForm.herbName) {
        alert('请填写必填项：药企名称和中药材品种')
        return
      }
      this.adding = true
      try {
        const response = await axios.post('/api/gap/create', this.addForm, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          alert('添加成功！')
          this.showAddModal = false
          this.resetAddForm()
          this.fetchList()
          this.fetchHerbsList()
        } else {
          alert(response.data?.msg || '添加失败')
        }
      } catch (err) {
        console.error('添加失败:', err)
        alert('添加失败，请稍后重试')
      } finally {
        this.adding = false
      }
    },
    resetAddForm() {
      this.addForm = {
        companyName: '',
        herbName: '',
        originOrBatch: '',
        medicineVariety: '',
        supplierName: '',
        baseArea: '',
        baseAddress: '',
        inspectionResult: '符合要求',
        noticeTime: '',
        announceTime: '',
        inspectionBureau: '',
        yieldAmount: ''
      }
    },
    async handleDelete(id) {
      if (!confirm('确定要删除这条记录吗？')) return
      try {
        const response = await axios.delete(`/api/gap/${id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          alert('删除成功！')
          this.fetchList()
          this.fetchHerbsList()
        } else {
          alert(response.data?.msg || '删除失败')
        }
      } catch (err) {
        console.error('删除失败:', err)
        alert('删除失败，请稍后重试')
      }
    },
    async handleExcelUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const data = await file.arrayBuffer()
        const workbook = XLSX.read(data, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 })

        const headers = rows[0] || []
        const companyIdx = headers.findIndex(h => String(h).includes('药企') || String(h).includes('企业') || String(h).includes('公司'))
        const herbIdx = headers.findIndex(h => String(h).includes('药材') || String(h).includes('品种'))
        const originIdx = headers.findIndex(h => String(h).includes('基原') || String(h).includes('批号'))
        const medicineIdx = headers.findIndex(h => String(h).includes('中药品种'))
        const supplierIdx = headers.findIndex(h => String(h).includes('生产企业'))
        const areaIdx = headers.findIndex(h => String(h).includes('面积'))
        const addressIdx = headers.findIndex(h => String(h).includes('地址'))
        const resultIdx = headers.findIndex(h => String(h).includes('检查结果') || String(h).includes('结果'))
        const noticeIdx = headers.findIndex(h => String(h).includes('公示'))
        const announceIdx = headers.findIndex(h => String(h).includes('公布'))
        const yieldIdx = headers.findIndex(h => String(h).includes('产量'))
        const bureauIdx = headers.findIndex(h => String(h).includes('检查局') || String(h).includes('监管'))

        // Excel 日期单元格默认解析为序列号数字(如 46199 = 2026-07-16),
        // 需要手动转换为日期字符串;文本型日期(如 "2023年\n11月24日")则清理换行。
        const formatExcelDate = (v) => {
          if (v === null || v === undefined || v === '') return ''
          // Date 对象(若调用方开启 cellDates 时)
          if (v instanceof Date && !isNaN(v)) {
            return `${v.getFullYear()}年${v.getMonth() + 1}月${v.getDate()}日`
          }
          // 数字 = Excel 1900日期系统序列号,25569 对应 1970-01-01
          if (typeof v === 'number' && isFinite(v)) {
            const d = new Date(Math.round((v - 25569) * 86400 * 1000))
            if (!isNaN(d)) {
              return `${d.getUTCFullYear()}年${d.getUTCMonth() + 1}月${d.getUTCDate()}日`
            }
          }
          // 文本型日期:去除换行符与首尾空白
          return String(v).replace(/\r?\n/g, '').trim()
        }

        const importData = []
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i]
          if (!row) continue
          const item = {
            company_name: companyIdx !== -1 && row[companyIdx] ? String(row[companyIdx]).trim() : '',
            herb_name: herbIdx !== -1 && row[herbIdx] ? String(row[herbIdx]).trim() : '',
            origin_or_batch: originIdx !== -1 && row[originIdx] ? String(row[originIdx]).trim() : '',
            medicine_variety: medicineIdx !== -1 && row[medicineIdx] ? String(row[medicineIdx]).trim() : '',
            supplier_name: supplierIdx !== -1 && row[supplierIdx] ? String(row[supplierIdx]).trim() : '',
            base_area: areaIdx !== -1 && row[areaIdx] ? String(row[areaIdx]).trim() : '',
            base_address: addressIdx !== -1 && row[addressIdx] ? String(row[addressIdx]).trim() : '',
            inspection_result: resultIdx !== -1 && row[resultIdx] ? String(row[resultIdx]).trim() : '',
            notice_time: noticeIdx !== -1 ? formatExcelDate(row[noticeIdx]) : '',
            announce_time: announceIdx !== -1 ? formatExcelDate(row[announceIdx]) : '',
            yield_amount: yieldIdx !== -1 && row[yieldIdx] ? String(row[yieldIdx]).trim() : '',
            inspection_bureau: bureauIdx !== -1 && row[bureauIdx] ? String(row[bureauIdx]).trim() : ''
          }
          if (item.company_name || item.herb_name) {
            importData.push(item)
          }
        }

        if (importData.length === 0) {
          alert('Excel没有有效数据')
          return
        }

        const res = await axios.post('/api/gap/batch-import', 
          { data: importData },
          { headers: { 'x-admin-password': getAdminPassword() } }
        )

        if (res.data?.code === 200) {
          const d = res.data.data || {}
          const imported = d.imported ?? importData.length
          const skipped = d.skipped ?? 0
          if (skipped > 0) {
            alert(`导入完成：新增 ${imported} 条，跳过已存在 ${skipped} 条`)
          } else {
            alert(`导入成功！共 ${imported} 条`)
          }
          this.fetchList()
          this.fetchHerbsList()
        } else {
          alert('导入失败：' + res.data?.msg)
        }
      } catch (err) {
        console.error('Excel解析错误:', err)
        alert('Excel解析失败：' + err.message)
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

.main-content {
  flex: 1;
  margin-left: 260px;
  padding: 24px;
  overflow-y: auto;
  height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 26px;
  color: #2c3e50;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 16px;
}

.total-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  flex-wrap: wrap;
}

.search-group {
  display: flex;
  gap: 12px;
}

.search-input {
  padding: 10px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  width: 220px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
}

.search-btn, .refresh-btn, .reset-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.search-btn:hover, .refresh-btn:hover, .reset-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102,126,234,0.3);
}

.reset-btn {
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
}

.refresh-btn {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item label {
  font-size: 14px;
  color: #7f8c8d;
  font-weight: 500;
}

.filter-select {
  padding: 10px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 150px;
  transition: all 0.3s ease;
}

.filter-select:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
}

.filter-actions {
  display: flex;
  gap: 8px;
}

.content-body {
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #667eea;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e1e5e9;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  text-align: center;
  padding: 60px;
  color: #e74c3c;
  font-size: 16px;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1400px;
}

.data-table th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 14px 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  font-size: 13px;
  white-space: nowrap;
}

.data-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  font-size: 13px;
}

.data-table tr:hover {
  background: #f8f9fa;
}

/* 列宽控制 */
.th-id, .td-id { width: 60px; text-align: center; }
.th-company, .td-company { min-width: 180px; }
.th-herb, .td-herb { min-width: 100px; }
.th-origin, .td-origin { min-width: 120px; }
.th-medicine, .td-medicine { min-width: 140px; }
.th-supplier, .td-supplier { min-width: 180px; }
.th-area, .td-area { width: 100px; }
.th-address, .td-address { min-width: 200px; max-width: 300px; }
.th-result, .td-result { width: 100px; }
.th-notice, .td-notice { min-width: 120px; }
.th-announce, .td-announce { min-width: 120px; }
.th-yield, .td-yield { width: 100px; }
.th-bureau, .td-bureau { min-width: 160px; }
.th-action, .td-action { width: 80px; text-align: center; }

/* 删除按钮 */
.delete-btn {
  padding: 6px 12px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s ease;
}

.delete-btn:hover {
  background: #c82333;
}

/* 地址列样式 */
.td-address {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.td-address:hover {
  white-space: normal;
  overflow: visible;
}

/* 药材标签 */
.herb-tag {
  display: inline-block;
  padding: 4px 12px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

/* 面积样式 */
.area-value {
  font-weight: 600;
  color: #2c3e50;
}

.area-unit {
  color: #6c757d;
  font-size: 12px;
  margin-left: 4px;
}

/* 检查结果标签 */
.status-tag {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.passed {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-tag.failed {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status-tag.other {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  color: #856404;
  border: 1px solid #ffeeba;
}

.empty {
  text-align: center;
  padding: 60px;
  color: #6c757d;
  font-size: 16px;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
  border-top: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #6c757d;
  font-size: 14px;
}

.page-size-select {
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 13px;
  color: #495057;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-size-select:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  padding: 8px 16px;
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #495057;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102,126,234,0.15);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  padding: 8px 14px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  font-size: 13px;
  color: #495057;
  font-weight: 500;
  white-space: nowrap;
}

.page-jump {
  font-size: 13px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-input {
  width: 52px;
  padding: 6px 10px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
  color: #495057;
  transition: all 0.3s ease;
}

.page-input:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
}

/* 添加按钮样式 */
.add-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.upload-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.4);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body {
  padding: 24px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.form-item {
  flex: 1;
}

.form-item.full-width {
  flex: 1 1 100%;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.form-item label::after {
  content: '';
}

.form-item input,
.form-item select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.form-item input:focus,
.form-item select:focus,
.form-item textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-item textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.cancel-btn {
  padding: 10px 20px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s ease;
}

.cancel-btn:hover {
  background: #5a6268;
}

.submit-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>